import { AuthConfiguration, UiFlags } from '@tassat/ui-definitions';

import encryptDecrypt from '../encryptDecrypt';
import { StorageType } from './types';

const decodeJwtSub = (token: string): string | undefined => {
    try {
        const payload = token.split('.')[1]
            .replace(/-/g, '+')
            .replace(/_/g, '/');

        return JSON.parse(atob(payload))?.sub;
    } catch {
        return undefined;
    }
};

class BrowserStorage {
    storageType: StorageType;
    private static JWT_SALT: string;
    private static TOKEN_SALT: string;

    constructor(storageType: StorageType) {
        this.storageType = storageType;
    }

    static async configurations(getConfig): Promise<void> {
        const authConfig: AuthConfiguration = await getConfig();
        this.JWT_SALT = authConfig.JWT_SALT;
        this.TOKEN_SALT = authConfig.TOKEN_SALT;
    }

    private storage(): Storage | Record<string, string> {
        switch (this.storageType) {
            case (StorageType.Cookie):
                return document.cookie.split('; ')
                    .reduce((accumulator, pair) => {
                        const [ key, value ] = pair.split('=');
                        accumulator[key] = value;

                        return accumulator;
                    }, {});
            case (StorageType.LocalStorage):
                return window.localStorage;
            case (StorageType.SessionStorage):
                return window.sessionStorage;
            default:
                throw Error('Storage type not specified.');
        }
    }

    getItem(key: string): string | null {
        const storage = this.storage();

        switch (this.storageType) {
            case (StorageType.Cookie):
                return storage[key] ?? null;
            case (StorageType.LocalStorage):
            case (StorageType.SessionStorage): {
                if (key === 'id_token') {
                    const idToken = sessionStorage.getItem('id_token');
                    if (!idToken) return null;

                    return (
                        UiFlags.NO_ID_TOKEN_ENCRYPTION in localStorage &&
                        localStorage.getItem(UiFlags.NO_ID_TOKEN_ENCRYPTION) === decodeJwtSub(idToken)
                    ) ?
                        idToken
                        : encryptDecrypt.decrypt(
                            idToken,
                            browserSessionStore.getItem('expires_at') as string,
                            BrowserStorage.JWT_SALT
                        );
                } else if (key === 'refresh_token') {
                    const refreshToken = sessionStorage.getItem('refresh_token');
                    if (!refreshToken) return null;

                    return encryptDecrypt.decrypt(
                        sessionStorage.getItem('refresh_token') as string,
                        browserSessionStore.getItem('expires_at') as string,
                        BrowserStorage.TOKEN_SALT
                    );
                } else if (key === 'userid') {
                    return sessionStorage.getItem('userid') ? window.atob(sessionStorage.getItem('userid') as string) : null;
                } else if (key === 'auth_config') {
                    const authConfig = sessionStorage.getItem('auth_config');
                    if (!authConfig) return null;

                    return encryptDecrypt.decrypt(
                        authConfig,
                        import.meta.env.AUTH_KEY_PART,
                        import.meta.env.AUTH_SALT
                    );
                }

                return (storage as Storage).getItem(key);
            }
            default:
                return null;
        }
    }

    setItem(key: string, value: string): void {
        const storage = this.storage();

        switch (this.storageType) {
            case (StorageType.Cookie):
                document.cookie = `${key}=${value}; path=/;`;
                break;
            case (StorageType.LocalStorage):
            case (StorageType.SessionStorage): {
                if (key === 'id_token') {
                    value = (
                        UiFlags.NO_ID_TOKEN_ENCRYPTION in localStorage &&
                        localStorage.getItem(UiFlags.NO_ID_TOKEN_ENCRYPTION) === decodeJwtSub(value)
                    ) ?
                        value
                        : encryptDecrypt.encrypt(
                            value,
                            sessionStorage.getItem('expires_at') as string,
                            BrowserStorage.JWT_SALT
                        );
                } else if (key === 'refresh_token') {
                    value = encryptDecrypt.encrypt(
                        value,
                        sessionStorage.getItem('expires_at') as string,
                        BrowserStorage.TOKEN_SALT
                    );
                } else if (key === 'userid') {
                    value = window.btoa(value);
                } else if (key === 'auth_config') {
                    value = encryptDecrypt.encrypt(
                        value,
                        import.meta.env.AUTH_KEY_PART,
                        import.meta.env.AUTH_SALT
                    );
                }

                (storage as Storage).setItem(key, value);
                break;
            }
        }
    }

    removeItem(key: string): void {
        const storage = this.storage();

        switch (this.storageType) {
            case (StorageType.Cookie):
                document.cookie = `${key}=; expires=${new Date(0)}; path=/;`;
                break;
            case (StorageType.LocalStorage):
            case (StorageType.SessionStorage):
                (storage as Storage).removeItem(key);
                break;
        }
    }

    includes(key: string): boolean {
        const storage = this.storage();

        switch (this.storageType) {
            case (StorageType.Cookie):
            case (StorageType.LocalStorage):
            case (StorageType.SessionStorage):
                return key in storage;
            default:
                return false;
        }
    }

    setItems(record: Record<string, string>): void {
        for (const [ key, value ] of Object.entries(record)) {
            this.setItem(key, value);
        }
    }

    removeItems(keys: string[]): void {
        keys.forEach((key: string) => {
            this.removeItem(key);
        });
    }

    removeAllSessionStorage(): void {
        this.removeItems([
            'access_token',
            'id_token',
            'expires_at',
            'refresh_token',
            'browser_refresh',
            'refresher',
            'advisoryAck',
            'is_admin',
            'userid',
            'is_sso',
            'auth_config'
        ]);
    }
}

const browserSessionStore = new BrowserStorage(StorageType.SessionStorage);
const browserLocalStore = new BrowserStorage(StorageType.LocalStorage);

export {
    browserLocalStore,
    browserSessionStore
};

export default BrowserStorage;